import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Printer } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TicketsPrintPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [eventRes, codesRes] = await Promise.all([
          axios.get(`${API_URL}/api/events/${eventId}`),
          axios.get(`${API_URL}/api/discounts/${eventId}/codes`),
        ]);
        setEvent(eventRes.data);
        setCodes(codesRes.data || []);
      } catch (e) {
        // silent fail, let UI show empty
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  useEffect(() => {
    // Auto-open print dialog when loaded
    if (!loading && event) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [loading, event]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-[#00CC66] animate-spin" />
      </div>
    );
  }

  const dateStr = event?.start_date
    ? new Date(event.start_date).toLocaleDateString('es-CL', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
    : '—';

  const timeStr = event?.start_date
    ? new Date(event.start_date).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
    : '';

  const printedAt = new Date().toLocaleString('es-CL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="bg-white min-h-screen">
      <style>{`
        @page { size: A4; margin: 18mm 14mm 14mm 14mm; }
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .ticket-row { break-inside: avoid; }
      `}</style>

      {/* Toolbar (not printed) */}
      <div className="no-print sticky top-0 z-10 bg-[#064E3B] text-white px-6 py-3 flex items-center justify-between shadow-md">
        <div className="text-sm">
          Vista previa de impresión — se abrirá el diálogo automáticamente
        </div>
        <button onClick={() => window.print()}
                className="bg-[#00CC66] hover:bg-[#00b359] text-white rounded-full px-4 py-1.5 text-sm font-bold flex items-center gap-2"
                data-testid="print-again-btn">
          <Printer className="w-4 h-4" /> Imprimir
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-8">
        {/* Header with PlanCity logo */}
        <header className="flex items-start justify-between pb-6 border-b-2 border-[#00CC66] mb-6">
          <div>
            <img src="/logo_plancity_dark.png" alt="PlanCity" className="h-12 mb-3" />
            <div className="text-[10px] uppercase tracking-[3px] text-[#00CC66] font-bold">Listado de tickets promocionales</div>
          </div>
          <div className="text-right text-xs text-gray-500">
            <div>Impreso: {printedAt}</div>
            <div className="mt-0.5">plancity.cl</div>
          </div>
        </header>

        {/* Event info */}
        <section className="mb-6">
          <h1 className="text-3xl font-black text-[#064E3B] leading-tight mb-2">{event?.name || 'Evento'}</h1>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-700">
            <div><strong className="text-[#064E3B]">Fecha:</strong> {dateStr}{timeStr ? ` · ${timeStr}` : ''}</div>
            {event?.location && <div><strong className="text-[#064E3B]">Lugar:</strong> {event.location}</div>}
          </div>
          <div className="mt-3 inline-block bg-[#00CC66]/10 text-[#00CC66] border border-[#00CC66]/30 rounded-full px-3 py-1 text-xs font-bold">
            Total de tickets generados: {codes.length}
          </div>
        </section>

        {/* Tickets list table */}
        {codes.length === 0 ? (
          <div className="text-center py-20 text-gray-400 text-sm border border-dashed border-gray-300 rounded-xl">
            No hay tickets generados para este evento.
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#064E3B] text-white text-xs uppercase tracking-wider">
                <th className="text-left py-2.5 px-3 font-bold w-12">#</th>
                <th className="text-left py-2.5 px-3 font-bold">Código del ticket</th>
                <th className="text-left py-2.5 px-3 font-bold w-32">Generado</th>
                <th className="text-center py-2.5 px-3 font-bold w-20">Usado</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c, idx) => {
                const isUsed = !!c.used_at;
                const gen = c.created_at ? new Date(c.created_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }) : '—';
                return (
                  <tr key={c.id} className={`ticket-row border-b border-gray-200 ${isUsed ? 'bg-[#00CC66]/5' : ''}`}>
                    <td className="py-3 px-3 text-sm text-gray-500 font-mono">{String(idx + 1).padStart(3, '0')}</td>
                    <td className="py-3 px-3 font-mono font-black tracking-wider text-base text-[#064E3B]">
                      {c.code}
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-500">{gen}</td>
                    <td className="py-3 px-3 text-center">
                      {isUsed ? (
                        <div className="inline-block w-5 h-5 bg-[#00CC66] rounded border-2 border-[#00CC66] text-white text-sm font-black leading-none" style={{ lineHeight: '14px' }}>✓</div>
                      ) : (
                        <div className="inline-block w-5 h-5 border-2 border-gray-400 rounded" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <footer className="mt-8 pt-4 border-t border-gray-200 text-[10px] text-gray-400 text-center">
          Documento generado por PlanCity · Uso interno del proveedor · plancity.cl
        </footer>
      </div>
    </div>
  );
};

export default TicketsPrintPage;
